import React, { useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Filter,
  FilterX,
  SortAsc,
  SortDesc,
  MoreHorizontal,
  RotateCcw,
  X,
  ChevronDown,
  Users2,
  Settings,
} from "lucide-react";
import { DualGroupingSelector } from "../../DualGroupingSelector";
import { GroupingOptionsSelector } from "../../GroupingOptionsSelector";
import { QuickFiltersPopover } from "../QuickFiltersPopover";
import type { SimpleTableConfig, BaseEntity, FieldValue } from "../../types";
import type { GroupableField, GroupingContext } from "../../../GroupingWrapper/types";

interface SimpleTableHeaderFilterControlsProps<TData extends BaseEntity> {
  config: SimpleTableConfig<TData>;
  activeFilterCount: number;
  onClearAll: () => void;
  sortColumns: Array<{ field: string; direction: "asc" | "desc"; priority: number }>;
  onRemoveSortColumn: (field: string) => void;
  onClearAllSorting: () => void;
  onResetColumnWidths: () => void;
  advancedFilterCount: number;
  groupableFields?: GroupableField[];
  groupingContext?: GroupingContext;
  /** Quick filters popover props - when provided, Filters button opens popover instead of dropdown */
  quickFiltersPopoverProps?: {
    simpleFilters: Record<string, unknown>;
    onFiltersChange: (newFilters: Record<string, unknown>) => void;
    onCustomRangeOpen: (fieldId: string, fieldLabel: string) => void;
    advancedFilterProps?: {
      advancedFilters:
        | Record<string, unknown>
        | Array<{ id: string; field: string; operator: string; value: unknown }>;
      filterOperator: "AND" | "OR";
      setFilterOperator: (operator: "AND" | "OR") => void;
      addAdvancedFilter: (fieldId: string, filterType: string, value: FieldValue) => void;
      removeAdvancedFilter: (fieldId: string) => void;
      clearAllAdvancedFilters: () => void;
    };
  };
}

/**
 * Filter control buttons and dropdowns for SimpleTableHeader
 * Includes: Filter toggle, Clear All, Grouping, More Options (sorts, column widths, advanced filters)
 */
export function SimpleTableHeaderFilterControls<TData extends BaseEntity>({
  config,
  activeFilterCount,
  onClearAll,
  sortColumns,
  onRemoveSortColumn,
  onClearAllSorting,
  onResetColumnWidths,
  advancedFilterCount,
  groupableFields,
  groupingContext,
  quickFiltersPopoverProps,
}: SimpleTableHeaderFilterControlsProps<TData>) {
  const [filtersPopoverOpen, setFiltersPopoverOpen] = useState(false);
  const [openInAdvancedMode, setOpenInAdvancedMode] = useState(false);

  const handlePrimaryGroupingChange = useCallback(
    (field: string | null) => {
      groupingContext?.setGroupByField?.(field);
    },
    [groupingContext]
  );

  const handleSecondaryGroupingChange = useCallback(
    (field: string | null) => {
      groupingContext?.setDualGrouping?.(groupingContext?.groupByField || null, field);
    },
    [groupingContext]
  );

  const handleClearGrouping = useCallback(() => {
    groupingContext?.setGroupByField?.(null);
    groupingContext?.setDualGrouping?.(null, null);
  }, [groupingContext]);

  const filtersButton = (
    <Button
      variant={filtersPopoverOpen ? "secondary" : "outline"}
      size="sm"
      className="flex items-center gap-2 h-10"
    >
      <Filter className="h-4 w-4" />
      <span>Filters</span>
      {activeFilterCount > 0 && (
        <Badge
          variant="default"
          className="ml-1 h-5 min-w-[20px] px-1.5 bg-primary text-primary-foreground"
        >
          {activeFilterCount}
        </Badge>
      )}
      <ChevronDown className="h-3.5 w-3.5 ml-1" />
    </Button>
  );

  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      {config.features?.filters &&
        (quickFiltersPopoverProps ? (
          <QuickFiltersPopover
            config={config}
            simpleFilters={quickFiltersPopoverProps.simpleFilters}
            onFiltersChange={quickFiltersPopoverProps.onFiltersChange}
            onCustomRangeOpen={quickFiltersPopoverProps.onCustomRangeOpen}
            activeFilterCount={activeFilterCount}
            advancedFilterCount={advancedFilterCount}
            trigger={filtersButton}
            open={filtersPopoverOpen}
            onOpenChange={setFiltersPopoverOpen}
            openInAdvancedMode={openInAdvancedMode}
            onConsumedOpenInAdvancedMode={() => setOpenInAdvancedMode(false)}
            advancedFilterProps={quickFiltersPopoverProps.advancedFilterProps}
          />
        ) : (
          filtersButton
        ))}

      {/* Group Button */}
      {groupableFields && groupableFields.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={groupingContext?.isGrouped ? "secondary" : "outline"}
              size="sm"
              className="flex items-center gap-2 h-10"
              title="Group data by field"
            >
              <Users2 className="h-4 w-4" />
              <span className="hidden sm:inline">Group</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-80">
            <DropdownMenuLabel>Grouping Options</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="p-3">
              <DualGroupingSelector
                primaryField={groupingContext?.groupByField || null}
                secondaryField={groupingContext?.dualGrouping?.secondary || null}
                groupableFields={groupableFields}
                onPrimaryChange={handlePrimaryGroupingChange}
                onSecondaryChange={handleSecondaryGroupingChange}
                onClear={handleClearGrouping}
              />
            </div>

            {/* Grouping Options for selected field */}
            {(groupingContext?.groupByField || groupingContext?.dualGrouping?.primary) && (
              <>
                <DropdownMenuSeparator />
                <div className="p-3">
                  {(() => {
                    const selectedField = groupableFields?.find(
                      (f) =>
                        f.fieldId ===
                        (groupingContext?.groupByField || groupingContext?.dualGrouping?.primary)
                    );
                    return (
                      <GroupingOptionsSelector
                        fieldType={selectedField?.type || ""}
                        fieldLabel={selectedField?.label || ""}
                        groupingOptions={selectedField?.groupingOptions || undefined}
                      />
                    );
                  })()}
                </div>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {activeFilterCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAll}
          className="h-10 text-xs text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
          title="Clear all filters and search"
        >
          <FilterX className="h-3.5 w-3.5 mr-1.5" />
          Clear All
        </Button>
      )}

      {/* More Options Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="flex-shrink-0 h-10" title="More options">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>View Options</DropdownMenuLabel>
          <DropdownMenuSeparator />

          {config.features?.filters && quickFiltersPopoverProps?.advancedFilterProps && (
            <>
              <DropdownMenuItem
                onClick={() => {
                  setFiltersPopoverOpen(true);
                  setOpenInAdvancedMode(true);
                }}
              >
                <Settings className="h-4 w-4 mr-2" />
                <span>Advanced Filters</span>
                {advancedFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-auto h-5 min-w-[20px] px-1.5">
                    {advancedFilterCount}
                  </Badge>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          {sortColumns.length > 0 && (
            <>
              <DropdownMenuLabel className="text-xs font-semibold text-gray-500 uppercase px-2">
                Active Sorts ({sortColumns.length})
              </DropdownMenuLabel>
              {sortColumns
                .sort((a, b) => a.priority - b.priority)
                .map((sortCol) => (
                  <DropdownMenuItem
                    key={sortCol.field}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveSortColumn(sortCol.field);
                    }}
                    className="justify-between text-sm"
                  >
                    <div className="flex items-center gap-2">
                      {sortCol.direction === "asc" ? (
                        <SortAsc className="h-3 w-3" />
                      ) : (
                        <SortDesc className="h-3 w-3" />
                      )}
                      <span>{config.fields[sortCol.field]?.label || sortCol.field}</span>
                    </div>
                    <X className="h-3 w-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-400" />
                  </DropdownMenuItem>
                ))}
              <DropdownMenuItem onClick={onClearAllSorting}>
                <FilterX className="h-3.5 w-3.5 mr-2" />
                Clear All Sorting
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          {config.features?.columnResizing && (
            <DropdownMenuItem onClick={onResetColumnWidths}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset Column Widths
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
